"use client";

import { getImageWithFallback } from "@/lib/default-image";
import Image from "next/image";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import type { Swiper as SwiperType } from "swiper";
import { A11y, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";

function Icon({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

/**
 * Swipeable photo carousel (Swiper): drag / swipe on every device, arrows on
 * hover for desktop. Safe inside a <Link>: a swipe or an arrow never opens the
 * link (Swiper swallows the click after a drag), and it works inside another
 * Swiper row: the outer row skips `.swiper-no-swiping` (set on this wrapper),
 * while this carousel listens for its own, unused no-swiping class.
 */
export default function ImageCarousel({
  images,
  alt,
  className = "",
  imgClassName = "",
  sizes = "100vw",
  priority = false,
  counter = false,
  isAr = true,
  onIndexChange,
}: {
  images: string[];
  alt: string;
  className?: string;
  imgClassName?: string;
  sizes?: string;
  priority?: boolean;
  /** "3 / 12" badge instead of dots */
  counter?: boolean;
  isAr?: boolean;
  onIndexChange?: (i: number) => void;
}) {
  const list = images.length ? images : [getImageWithFallback(null, "property")];
  const swiper = useRef<SwiperType | null>(null);
  const [index, setIndex] = useState(0);
  useEffect(() => onIndexChange?.(index), [index, onIndexChange]);

  const go = (e: MouseEvent, dir: -1 | 1) => {
    e.preventDefault();
    e.stopPropagation();
    if (dir < 0) swiper.current?.slidePrev();
    else swiper.current?.slideNext();
  };

  // At most 5 dots (like the apps): a window around the active one.
  const dots = (() => {
    const n = list.length;
    if (n <= 1) return [];
    const start = Math.max(0, Math.min(index - 2, n - 5));
    return Array.from({ length: Math.min(5, n) }, (_, k) => start + k);
  })();

  // Arrows follow reading direction: "next" points left in Arabic.
  const prevIcon = isAr ? "chevron_right" : "chevron_left";
  const nextIcon = isAr ? "chevron_left" : "chevron_right";

  return (
    <div className={`swiper-no-swiping group/carousel relative overflow-hidden ${className}`}>
      <Swiper
        key={isAr ? "rtl" : "ltr"} // Swiper reads the direction once
        dir={isAr ? "rtl" : "ltr"}
        modules={[A11y, Keyboard]}
        nested
        noSwipingClass="dh-carousel-no-swipe"
        slidesPerView={1}
        speed={350}
        resistanceRatio={0.5}
        keyboard={{ enabled: true, onlyInViewport: true }}
        a11y={{
          prevSlideMessage: isAr ? "الصورة السابقة" : "Previous photo",
          nextSlideMessage: isAr ? "الصورة التالية" : "Next photo",
        }}
        onSwiper={(s) => (swiper.current = s)}
        onSlideChange={(s) => setIndex(s.activeIndex)}
        className="h-full w-full"
      >
        {list.map((src, i) => (
          <SwiperSlide key={src + i} className="relative h-full! w-full">
            <Image
              src={getImageWithFallback(src, "property")}
              alt={i === 0 ? alt : `${alt} ${i + 1}`}
              fill
              sizes={sizes}
              unoptimized
              draggable={false}
              priority={priority && i === 0}
              loading={priority && i === 0 ? "eager" : "lazy"}
              className={`object-cover select-none ${imgClassName}`}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {list.length > 1 && (
        <>
          {/* Arrows — desktop hover only (phones swipe) */}
          {index > 0 && (
            <button
              type="button"
              aria-label={isAr ? "الصورة السابقة" : "Previous photo"}
              onClick={(e) => go(e, -1)}
              className="absolute top-1/2 inset-s-3 z-10 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-shade opacity-0 shadow-md transition group-hover/carousel:opacity-100 hover:scale-105 md:flex"
            >
              <Icon name={prevIcon} className="text-[20px]" />
            </button>
          )}
          {index < list.length - 1 && (
            <button
              type="button"
              aria-label={isAr ? "الصورة التالية" : "Next photo"}
              onClick={(e) => go(e, 1)}
              className="absolute top-1/2 inset-e-3 z-10 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-shade opacity-0 shadow-md transition group-hover/carousel:opacity-100 hover:scale-105 md:flex"
            >
              <Icon name={nextIcon} className="text-[20px]" />
            </button>
          )}

          {counter ? (
            <span className="pointer-events-none absolute bottom-3 inset-e-3 z-10 rounded-full bg-shade/70 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">
              {/* dir on the text only — on the badge itself it would flip which side "end" is */}
              <span dir="ltr">
                {index + 1} / {list.length}
              </span>
            </span>
          ) : (
            <div className="pointer-events-none absolute inset-x-0 bottom-2.5 z-10 flex items-center justify-center gap-1.5">
              {dots.map((d) => (
                <span
                  key={d}
                  className={`rounded-full bg-white shadow transition-all ${d === index ? "size-2 opacity-100" : "size-1.5 opacity-60"}`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
